/**
 * Cài đặt thông báo riêng trình duyệt này (localStorage, không lưu BE):
 *   - amThanh: phát tiếng "ting" khi có thông báo mới.
 *   - heThong: hiện thông báo của hệ điều hành khi tab đang ẩn (Notification API).
 */
import { useCallback, useEffect, useState } from 'react'

export interface CaiDatTrinhDuyet {
  amThanh: boolean
  heThong: boolean
}

const KEY = 'azl.thongBao.caiDatTrinhDuyet'
const SU_KIEN_DOI = 'azl:thong-bao-cai-dat-trinh-duyet'
const MAC_DINH: CaiDatTrinhDuyet = { amThanh: true, heThong: false }

export const docCaiDatTrinhDuyet = (): CaiDatTrinhDuyet => {
  try {
    return { ...MAC_DINH, ...JSON.parse(localStorage.getItem(KEY) || '{}') }
  } catch {
    return MAC_DINH
  }
}

const ghiCaiDatTrinhDuyet = (caiDat: CaiDatTrinhDuyet) => {
  localStorage.setItem(KEY, JSON.stringify(caiDat))
  window.dispatchEvent(new Event(SU_KIEN_DOI))
}

export const useCaiDatTrinhDuyet = () => {
  const [caiDat, setCaiDat] = useState(docCaiDatTrinhDuyet)

  useEffect(() => {
    const capNhat = () => setCaiDat(docCaiDatTrinhDuyet())
    window.addEventListener(SU_KIEN_DOI, capNhat)
    window.addEventListener('storage', capNhat)
    return () => {
      window.removeEventListener(SU_KIEN_DOI, capNhat)
      window.removeEventListener('storage', capNhat)
    }
  }, [])

  const doiCaiDat = useCallback((thayDoi: Partial<CaiDatTrinhDuyet>) => {
    ghiCaiDatTrinhDuyet({ ...docCaiDatTrinhDuyet(), ...thayDoi })
  }, [])

  return { caiDat, doiCaiDat }
}

export type QuyenThongBaoHeThong = 'default' | 'granted' | 'denied' | 'khong_ho_tro'

export const trinhDuyetHoTroThongBao = (): boolean => typeof window !== 'undefined' && 'Notification' in window

export const quyenThongBaoHeThong = (): QuyenThongBaoHeThong =>
  trinhDuyetHoTroThongBao() ? Notification.permission : 'khong_ho_tro'

/** Xin quyền Notification của trình duyệt. */
export const xinQuyenThongBaoHeThong = async (): Promise<QuyenThongBaoHeThong> => {
  if (!trinhDuyetHoTroThongBao()) return 'khong_ho_tro'
  if (Notification.permission !== 'default') return Notification.permission
  return Notification.requestPermission()
}

let audioCtx: AudioContext | null = null

/** Tiếng "ting" ngắn bằng Web Audio — không cần file âm thanh. */
export const phatAmThanhThongBao = () => {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    audioCtx = audioCtx ?? new Ctx()
    const ctx = audioCtx
    const bat = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, bat)
    osc.frequency.setValueAtTime(1320, bat + 0.12)
    gain.gain.setValueAtTime(0.0001, bat)
    gain.gain.exponentialRampToValueAtTime(0.25, bat + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, bat + 0.45)
    osc.connect(gain).connect(ctx.destination)
    osc.start(bat)
    osc.stop(bat + 0.5)
  } catch {
    // trình duyệt chặn autoplay trước khi user tương tác — bỏ qua
  }
}

/** Thông báo hệ điều hành (chỉ khi đã được cấp quyền). Bấm vào → focus tab + onClick. */
export const hienThongBaoHeThong = (tieuDe: string, noiDung: string, tag: string, onClick?: () => void) => {
  if (!trinhDuyetHoTroThongBao() || Notification.permission !== 'granted') return
  try {
    const n = new Notification(tieuDe, { body: noiDung, tag })
    n.onclick = () => {
      window.focus()
      onClick?.()
      n.close()
    }
  } catch {
    // một số trình duyệt (Android Chrome) chỉ cho tạo qua Service Worker
  }
}
